/**
 * The company this page actually belongs to.
 *
 * `~/data/site` (the `SITE` constant) is New Leaf Cleaning, hard-coded at build
 * time and imported by 103 files. That is fine for generic marketing copy — a
 * tagline, a founding year, the shape of a services list. It is NOT fine for
 * anything that IDENTIFIES the business to a real person: the company name, the
 * address they are told to contact, and above all the TCPA consent line on
 * /book-today, which asks a visitor to agree to calls and texts from a named
 * company. Shown on another tenant's domain, that names the wrong company in a
 * legal disclosure.
 *
 * So this module is deliberately narrow. It covers identity and contact only.
 * It is NOT the Premium white-label content system (`tenant-content.ts`, gated
 * by `requireFeature('white_label')`) — not impersonating a different real
 * business must never depend on the plan someone is paying for.
 *
 * Resolved once in the ROOT loader and read from route loader data, so it is
 * server-rendered and costs no extra query: /api/branding returns the tenant row
 * the middleware already loaded for the request.
 */

import { useRouteLoaderData } from 'react-router';
import { SITE } from '~/data/site';
import { resolveTheme, type TenantTheme } from '~/lib/theme';

export interface TenantBranding {
  id: string;
  name: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  /** The company's own uploaded logo, when it has one. Used as its favicon. */
  logoUrl?: string | null;
  /** The company's own colours, and whether it ever actually chose them. */
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  /** A key into @shared/fonts FONT_CHOICES. Null when never chosen. */
  brandFont?: string | null;
  brandingSetAt?: number | null;
}

/** The tenant New Leaf itself runs under. Used to decide what is "our own" copy. */
export const NLC_TENANT_ID = 'nlc';

/**
 * Hostnames that are genuinely New Leaf's own — its production domain plus the
 * legacy domain it redirects from. Distinct from `isNewLeaf` on
 * ResolvedBranding (which asks "does this tenant resolve to nlc", true even on
 * a broomstack.app subdomain): this asks "is this literally one of New
 * Leaf's own addresses", for content that should only exist THERE — a
 * one-off campaign landing page, first-party analytics tags, anything that
 * is not just "New Leaf's branding" but "New Leaf's own domain."
 */
export const NLC_HOSTS = new Set([
  'nlcstpete.com',
  'www.nlcstpete.com',
  'newleafcleaningservice.com',
  'www.newleafcleaningservice.com',
]);

export function isNlcOwnHost(hostname: string): boolean {
  return NLC_HOSTS.has(hostname.toLowerCase());
}

export interface ResolvedBranding {
  /**
   * The tenant id, or null when it could not be resolved (localhost, an
   * unrecognised host). This is what a login link is built from — every
   * tenant's id resolves as its own <id>.broomstack.app subdomain — so null
   * here means "fall back to a relative link", never a fabricated address.
   */
  id: string | null;
  /** The company name to show. Falls back to SITE for New Leaf and on failure. */
  name: string;
  /** Contact address, or null when this tenant has not set one. */
  email: string | null;
  /** Contact phone, or null when this tenant has not set one. */
  phone: string | null;
  /** `tel:` href for `phone`, or null. */
  phoneHref: string | null;
  /**
   * True when this really is New Leaf. Gate NLC-specific content on it —
   * testimonials that name the company, NLC's own review links, and anything
   * else that is a claim about New Leaf rather than about "this company".
   */
  isNewLeaf: boolean;
  /**
   * The favicon for THIS request.
   *
   * There was one favicon in this codebase — New Leaf's leaf — declared in
   * root.tsx's static `links`, so every company on the platform and the operator
   * console served it. A customer's staff opening their own admin console saw
   * another cleaning company's mark in the tab.
   *
   * Resolution order: the company's own logo → New Leaf's leaf ONLY when this
   * really is New Leaf → the neutral platform mark. Falling back to New Leaf's
   * icon on an unknown host is the same mistake as falling back to their phone
   * number, which is the whole reason this module exists.
   */
  iconHref: string;
  /** Same resolution, for the iOS home-screen icon. */
  appleIconHref: string;
  /**
   * The company's own logo, for the public nav and footer — or null.
   *
   * NULL IS A VALID ANSWER AND THE CALLER MUST HONOUR IT by rendering the
   * company's NAME as text. It must never fall back to another company's mark.
   * `/images/nlc-logo.png` was hard-coded into both `public-nav` and
   * `public-footer`, so Second Shift Cleaning's site showed New Leaf's leaf with
   * "Second Shift Cleaning" written beside it — a mark that reads as
   * deliberate co-branding rather than as a bug.
   *
   * Distinct from `iconHref`: that one always resolves to SOMETHING because a
   * browser tab needs an icon and the neutral platform mark is a fine answer
   * there. A wordmark is a better answer than a generic broom in the masthead.
   */
  logoUrl: string | null;
  /**
   * This company's public marketing nav, or empty.
   *
   * `SITE.navLinks` is New Leaf's RESIDENTIAL service menu — "Services",
   * "Pricing", "Reviews", "Service areas", all of which lead to pages written
   * about New Leaf. Rendered on `ssc.broomstack.app` it offered a COMMERCIAL
   * cleaner's visitors recurring house-cleaning and move-out cleans, and led
   * them into another company's copy.
   *
   * Empty is the honest answer for a tenant with no marketing site of its own:
   * the login menu and the quote CTA still render, because those are the
   * tenant's own. A per-tenant marketing nav belongs in `tenant-content.ts`
   * when that content actually exists.
   */
  navLinks: ReadonlyArray<{ label: string; href: string }>;
  /**
   * The company's colours, resolved. Falls back to the PLATFORM's neutral
   * palette, never to New Leaf's — see lib/theme.ts.
   */
  theme: TenantTheme;
  /**
   * The actual origin this request arrived on (`https://demo.broomstack.app`,
   * `https://nlcstpete.com`, ...) — read straight off the request URL in the
   * root loader, never guessed from the tenant's slug. A tenant can have a
   * custom_domain that differs from `<slug>.broomstack.app`, so guessing would
   * risk a canonical URL that points at a domain nobody is actually visiting.
   * Used for SEO tags (canonical, og:url) that must name the domain being
   * served, not New Leaf's — see `buildMeta()` in ~/lib/seo.
   */
  origin: string;
}

/** Digits-only `tel:` href. A phone number typed by a human is not a URL. */
function telHref(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
}

/**
 * Resolve a raw branding payload.
 *
 * Exported separately from the hook so pages that already hold the payload (or
 * that need this on the server) can use it without a React context.
 */
export function resolveBranding(
  branding: TenantBranding | null | undefined,
  /*
   * Whether the request arrived on one of New Leaf's own hostnames.
   *
   * Only consulted when `branding` is missing. Falling back to New Leaf on an
   * UNKNOWN host is the original bug — it is how another company's visitors were
   * shown New Leaf's phone number and asked to consent to texts from them. So a
   * failed lookup on a foreign host degrades to neutral rather than to us.
   */
  isNlcHost = true,
  /**
   * The real origin this request came in on (root loader's `url.origin`).
   * Defaults to SITE.url so callers that predate this field (or that render
   * with no request context, like an error boundary) keep working — that
   * default is only WRONG in the same direction the rest of this function
   * already falls back in when nothing better is known.
   */
  origin: string = SITE.url,
): ResolvedBranding {
  /*
   * The icons, in one place so the three return paths below cannot disagree.
   *
   * NLC_ICON is New Leaf's leaf and may ONLY be used when the request really is
   * New Leaf's. PLATFORM_ICON is the neutral mark — what we show when we do not
   * know whose icon it should be, which is the safe answer.
   */
  const NLC_ICON = '/images/favicon-32x32.png';
  const NLC_APPLE_ICON = '/images/apple-touch-icon.png';
  const PLATFORM_ICON = '/images/broomstack-icon.svg';
  /*
   * iOS does not rasterize SVG for `apple-touch-icon` — it silently falls
   * back to a screenshot of the page instead of the mark, on every tenant
   * that has not uploaded its own logo (confirmed live on ssc.broomstack.app,
   * 2026-09-21: iconHref/appleIconHref both resolved to the SVG). The plain
   * favicon tag is fine with SVG (Chrome/Firefox/modern Safari all support
   * it) — only the home-screen icon needs a raster fallback.
   */
  const PLATFORM_APPLE_ICON = '/images/broomstack-icon-180.png';
  /*
   * New Leaf's masthead logo. Its own branding in its own content, which is the
   * one case where a hard-coded asset is correct — and it is reachable ONLY
   * from the two New Leaf branches below. There is deliberately no "platform
   * logo" counterpart: a company with no logo gets its NAME, not our mark.
   */
  const NLC_LOGO = '/images/nlc-logo.png';

  // Nothing resolved. The hostname is the only thing left to go on.
  if (!branding) {
    return isNlcHost
      ? {
          id: NLC_TENANT_ID,
          name: SITE.name,
          email: SITE.contact.email,
          phone: null,
          phoneHref: null,
          isNewLeaf: true,
          iconHref: NLC_ICON,
          appleIconHref: NLC_APPLE_ICON,
          logoUrl: NLC_LOGO,
          navLinks: SITE.navLinks,
          // No row to read: NLC's own palette, which is what its row holds.
          theme: resolveTheme({
            primaryColor: '#4A7C59', secondaryColor: '#D4B896',
            accentColor: '#C5D8C7', brandingSetAt: 1,
          }),
          origin,
        }
      : // Known not to be New Leaf, and nothing else known. Say nothing rather
        // than assert someone else's identity. No id either — a login link
        // falls back to relative rather than guessing a subdomain.
        {
          id: null, name: 'this company', email: null, phone: null, phoneHref: null,
          isNewLeaf: false,
          iconHref: PLATFORM_ICON,
          appleIconHref: PLATFORM_APPLE_ICON,
          logoUrl: null,
          navLinks: [],
          theme: resolveTheme(null),
          origin,
        };
  }

  if (branding.id === NLC_TENANT_ID) {
    return {
      id: branding.id,
      name: branding.name ?? SITE.name,
      email: SITE.contact.email,
      phone: null,
      phoneHref: null,
      isNewLeaf: true,
      iconHref: NLC_ICON,
      appleIconHref: NLC_APPLE_ICON,
      logoUrl: NLC_LOGO,
      navLinks: SITE.navLinks,
      theme: resolveTheme(branding),
      origin,
    };
  }

  return {
    id: branding.id,
    // A tenant always has a name — it is required at provisioning.
    name: branding.name ?? 'this company',
    // Deliberately NOT falling back to SITE: handing another company's visitors
    // New Leaf's address is the bug, and a missing address is better than a
    // wrong one. Callers hide the line when this is null.
    email: branding.contactEmail,
    phone: branding.contactPhone,
    phoneHref: telHref(branding.contactPhone),
    isNewLeaf: false,
    // Their own logo if they have uploaded one; the neutral platform mark if not.
    // Never New Leaf's.
    iconHref: branding.logoUrl || PLATFORM_ICON,
    appleIconHref: branding.logoUrl || PLATFORM_APPLE_ICON,
    /*
     * No platform fallback here, unlike the favicon. A masthead with the
     * Broomstack mark on it would tell this company's customers they are
     * looking at our product rather than at their cleaner. Null means "render
     * the name", which is always right and always theirs.
     */
    logoUrl: branding.logoUrl || null,
    // No marketing pages of its own yet. Offering New Leaf's is the bug.
    navLinks: [],
    theme: resolveTheme(branding),
    origin,
  };
}

/**
 * Branding for the current request, from the root loader.
 *
 * Safe to call from any component under the root route, including ones rendered
 * on pages whose own loader knows nothing about tenancy.
 */
export function useBranding(): ResolvedBranding {
  const rootData = useRouteLoaderData('root') as
    | { branding?: TenantBranding | null; isNlcHost?: boolean; origin?: string }
    | undefined;
  return resolveBranding(rootData?.branding, rootData?.isNlcHost ?? true, rootData?.origin);
}

/**
 * Branding from a route's own `matches` array — for `meta()`/`links()` exports,
 * which run at module scope with no hooks available.
 *
 * A leaf route's own loader rarely resolves the tenant itself; the root loader
 * already did, and RR7 passes every ancestor match (root included) into
 * `MetaFunction({ matches })`. Reading it from there is the same data
 * `useBranding()` reads, without needing a duplicate loader per page.
 *
 * Usage: `export const meta: MetaFunction = ({ matches }) => { const b =
 * brandingFromMatches(matches); ... }`
 */
export function brandingFromMatches(
  matches: ReadonlyArray<{ id: string; data?: unknown } | null | undefined>,
): ResolvedBranding {
  const root = matches.find((m) => m?.id === 'root')?.data as
    | { branding?: TenantBranding | null; isNlcHost?: boolean; origin?: string }
    | undefined;
  return resolveBranding(root?.branding, root?.isNlcHost ?? true, root?.origin);
}
